import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useIntl } from "react-intl";
import useManuallyPost from "src/services/hooks/useManuallyPost";
import { apiUrls } from "./../../services/ApiService/constants";
import { AxiosError, AxiosResponse } from "axios";

type reporteesState = {
  name: string;
  id: string;
};

const RepresentationSelector = () => {
  const intl = useIntl();
  const [reportees, setReportees] = useState<reporteesState[]>();

  const handlePostError = useCallback((axiosError: AxiosError) => {
    window.alert(axiosError.message);
  }, []);

  const { loading, post } = useManuallyPost(handlePostError);

  useEffect(() => {
    const urlHash = window.location.hash;
    const splitOn = "reportee-selector?";
    if (urlHash.includes(splitOn)) {
      const params = new URLSearchParams(urlHash.split(splitOn)[1])
        .get("reportees")
        ?.split(",");
      const mappedReportees = params?.map((p) => {
        const [id, name] = p.split(";");
        return { name, id };
      });
      mappedReportees && setReportees(mappedReportees);
    }
  }, []);

  const redirectResult = useCallback((results: AxiosResponse) => {
    if (results?.data?.redirectUrl) {
      window.location.href = results.data.redirectUrl;
    }
  }, []);

  const selectReporteenCallback = useCallback(
    (id: string) => {
      post(apiUrls.selectReportee, { selectedReporteeId: id }, redirectResult);
    },
    [post, redirectResult]
  );

  const renderOptions = useCallback(() => {
    return reportees?.map(({ name, id }, index) => {
      return (
        <CustomButton
          key={index}
          disabled={loading}
          onClick={() => selectReporteenCallback(id)}
        >{`${name.toUpperCase()} (${id})`}</CustomButton>
      );
    });
  }, [reportees, loading, selectReporteenCallback]);

  return (
    <Container>
      <SelectorContainer>
        <Customh5>{intl.formatMessage({ id: "pick.prosecutor" })}</Customh5>
        <Selector>
          <Description>
            {intl.formatMessage({ id: "pick.prosecutor.description" })}
          </Description>
          {renderOptions()}
        </Selector>
      </SelectorContainer>
    </Container>
  );
};

const Description = styled.span`
  text-align: left;
  width: 100%;
  margin-bottom: 0.5em;
`;

const Customh5 = styled.h5`
  font-weight: 500;
  font-size: 1rem;
  margin-bottom: 0.5em;
`;

const Container = styled.section`
  padding: 40px 15px 70px 15px;
  @media only screen and (min-width: 1200px) {
    max-width: 1140px;
  }
  @media only screen and (max-width: 992px) {
    max-width: 960px;
  }
  @media only screen and (max-width: 768px) {
    max-width: 720px;
  }
  @media only screen and (max-width: 576px) {
    max-width: 540px;
  }
  margin: auto;
  margin-top: 0;
`;

const SelectorContainer = styled.div`
  text-align: left;
  margin: auto;
  display: flex;
  @media only screen and (min-width: ${({ theme }) => theme.screen.phone}) {
    width: 83%;
  }
  justify-content: space-between;
  flex-wrap: wrap;
  font-size: 1rem;
  padding-right: 15px;
  padding-left: 15px;
`;

const CustomButton = styled.button`
  text-align: left;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 0;
  padding: 0.6em 0.5em;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.font.text85};
  :hover {
    text-shadow: none;
    cursor: pointer;
  }
  width: 100%;
  font-family: inherit;
`;

const Selector = styled.div`
  margin: auto;
  padding: 0.5em;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  button:last-of-type {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background.primary};
  width: 100%;
`;

export default RepresentationSelector;
